// atm this api designed only for Webinars project and does not support second project(like webinars)
import { ComponentType, PropsWithChildren } from 'react';
import { ReactiveMap } from './reactive-map';

export type OverridableFunction = (this: unknown, ...args: any[]) => unknown;
export const COMPONENT_OVERRIDE_NAMES = new Set<string>();
export const FUNCTION_OVERRIDE_NAMES = new Set<string>();

export const overrideComponents = new ReactiveMap<string, ComponentType>();
export const overrideFunctions = new Map<string, OverridableFunction>();

export type OverrideDomain =
    | 'videoprocessing'
    | 'meeting-experience'
    | 'connected-modules'
    | 'collaborative-notes'
    | 'calendars-integration-service'
    | 'mls-integration';

type OverrideNamesMap = Record<OverrideDomain, Set<string>>;
export const COMPONENT_OVERRIDE_NAMES_MAP: OverrideNamesMap = {
    videoprocessing: new Set<string>(),
    'meeting-experience': new Set<string>(),
    'connected-modules': new Set<string>(),
    'collaborative-notes': new Set<string>(),
    'calendars-integration-service': new Set<string>(),
    'mls-integration': new Set<string>(),
};
export const FUNCTION_OVERRIDE_NAMES_MAP: OverrideNamesMap = {
    videoprocessing: new Set<string>(),
    'meeting-experience': new Set<string>(),
    'connected-modules': new Set<string>(),
    'collaborative-notes': new Set<string>(),
    'calendars-integration-service': new Set<string>(),
    'mls-integration': new Set<string>(),
};

export const RwcOverrideAPI = {
    overrideComponent(
        overrideName: string,
        component: ComponentType<PropsWithChildren<unknown>>
    ): typeof RwcOverrideAPI {
        overrideComponents.set(overrideName, component);

        return this;
    },

    overrideFunction(overrideName: string, func: OverridableFunction): typeof RwcOverrideAPI {
        overrideFunctions.set(overrideName, func);

        return this;
    },

    getOverridableComponentsList(domain?: OverrideDomain): string[] {
        if (domain) {
            return Array.from(COMPONENT_OVERRIDE_NAMES_MAP[domain]);
        }
        return Array.from(COMPONENT_OVERRIDE_NAMES);
    },

    getOverridableFunctionsList(domain?: OverrideDomain): string[] {
        if (domain) {
            return Array.from(FUNCTION_OVERRIDE_NAMES_MAP[domain]);
        }
        return Array.from(FUNCTION_OVERRIDE_NAMES);
    },

    resetOverrides(): typeof RwcOverrideAPI {
        overrideComponents.clear();
        overrideFunctions.clear();

        return this;
    },

    enableOverrides(): typeof RwcOverrideAPI {
        console.info('enableOverrides is deprecated');

        return this;
    },

    disableOverrides(): typeof RwcOverrideAPI {
        console.info('disableOverrides is deprecated');

        return this;
    },
};

export default RwcOverrideAPI;

export type RwcOverrideAPIType = typeof RwcOverrideAPI;

/**
 * Creates overridable function from given one
 * @param {function} fn
 * @param {string} overrideName
 * @param {OverrideDomain} domain
 * @returns {function}
 */
export function makeOverridableFunction<Fn extends OverridableFunction>(
    fn: Fn,
    overrideName: string,
    domain?: OverrideDomain
): Fn {
    if (typeof fn !== 'function') {
        throw new Error(`Received function override with name ${overrideName} with no function`);
    }

    if (domain) {
        if (FUNCTION_OVERRIDE_NAMES_MAP[domain].has(overrideName)) {
            throw new Error(
                `Override for function of ${domain} with name ${overrideName} already taken`
            );
        }
        FUNCTION_OVERRIDE_NAMES_MAP[domain].add(overrideName);
    }

    if (FUNCTION_OVERRIDE_NAMES.has(overrideName)) {
        throw new Error(`Override for function with name ${overrideName} already taken`);
    }
    FUNCTION_OVERRIDE_NAMES.add(overrideName);

    const overridableFunction = function (this: unknown, ...args: unknown[]) {
        return (overrideFunctions.get(overrideName ?? fn.name) ?? fn).apply(this, args);
    } as Fn & { displayName: string };

    const overridableFunctionName = `overridable_${
        fn.name ?? (fn as unknown as { displayName: string }).displayName ?? overrideName
    }`;
    Object.defineProperty(overridableFunction, 'name', { value: overridableFunctionName });
    overridableFunction.displayName = overridableFunctionName;

    return overridableFunction;
}

export function resetOverrides(): void {
    RwcOverrideAPI.resetOverrides();
}

export function overridesInit(overridesInit: (api: RwcOverrideAPIType) => unknown): boolean {
    if (typeof overridesInit !== 'function') {
        return false;
    }
    overridesInit(RwcOverrideAPI);
    return true;
}
